import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { studyPlanApi, competitiveExamsApi } from '../lib/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, Calendar, Loader2, CheckCircle2, Play,
  Flame, Sparkles, Target, BookOpen, Zap, RotateCcw, ChevronLeft, ChevronRight,
  Trash2, Edit3,
} from 'lucide-react';

const TYPE_META = {
  learning:   { label: 'Learning',          colour: 'bg-indigo-500',  icon: BookOpen },
  concept:    { label: 'Concept Clearance', colour: 'bg-rose-500',    icon: Zap },
  repetition: { label: 'Revision',          colour: 'bg-emerald-500', icon: RotateCcw },
  exam_prep:  { label: 'Exam Drill',        colour: 'bg-amber-500',   icon: Target },
};

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export default function StudyPlan() {
  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [plan, setPlan] = useState(null);
  const [showWizard, setShowWizard] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await studyPlanApi.current();
      setPlan(r.plan);
    } catch { /* empty */ }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="border-b border-border bg-card/50 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="sp-back">
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold flex items-center gap-2">
                <Calendar className="w-5 h-5 text-indigo-500" />Daily Learning Plan
              </h1>
              <p className="text-xs text-muted-foreground">Your coaching-class-style daily schedule — click a block and start studying.</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" onClick={toggleTheme}>
              {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="sm" onClick={logout}>Logout</Button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6">
        {showWizard || !plan ? (
          <Wizard existing={plan} onDone={() => { setShowWizard(false); load(); }} onCancel={() => setShowWizard(false)} />
        ) : (
          <PlannedView plan={plan} onEdit={() => setShowWizard(true)} onReset={load} />
        )}
      </div>
    </div>
  );
}

// ---------- Wizard ----------
function Wizard({ existing, onDone, onCancel }) {
  const [step, setStep] = useState(1);
  const [exams, setExams] = useState([]);
  const [form, setForm] = useState(() => ({
    exam_ids:  existing?.exam_ids || ['jee_main'],
    exam_date: existing?.exam_date || '',
    daily_hours_weekday: existing?.daily_hours_weekday ?? 3,
    daily_hours_weekend: existing?.daily_hours_weekend ?? 5,
    mix: existing?.mix || { learning: 40, concept: 20, repetition: 25, exam_prep: 15 },
    active_days: existing?.active_days || [0, 1, 2, 3, 4, 5, 6],
  }));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { competitiveExamsApi.getAll().then(setExams).catch(() => {}); }, []);

  const submit = async () => {
    if (!form.exam_ids.length) return toast.error('Pick at least one exam');
    setSubmitting(true);
    try {
      await studyPlanApi.create(form);
      toast.success('Plan saved — go crush it!');
      onDone();
    } catch (e) { toast.error(e.message || 'Save failed'); }
    setSubmitting(false);
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="sp-wizard">
      <div className="flex items-center gap-2 text-xs font-semibold text-muted-foreground">
        {[1, 2, 3, 4].map(n => (
          <div key={n} className={`h-1.5 flex-1 rounded-full ${n <= step ? 'bg-indigo-500' : 'bg-muted'}`} data-testid={`sp-step-${n}`} />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4" data-testid="sp-wizard-step-1">
          <div>
            <h2 className="text-xl font-bold">1 · Which exam are you preparing for?</h2>
            <p className="text-xs text-muted-foreground">You can pick more than one.</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {exams.slice(0, 16).map(e => (
              <button
                key={e.exam_id}
                onClick={() => {
                  const has = form.exam_ids.includes(e.exam_id);
                  setForm({
                    ...form,
                    exam_ids: has
                      ? form.exam_ids.filter(x => x !== e.exam_id)
                      : [...form.exam_ids, e.exam_id],
                  });
                }}
                data-testid={`sp-exam-${e.exam_id}`}
                className={`p-3 rounded-xl border text-left text-sm transition ${form.exam_ids.includes(e.exam_id) ? 'border-indigo-500 bg-indigo-50/40 dark:bg-indigo-950/20' : 'border-border hover:border-indigo-300'}`}
              >
                <p className="font-semibold">{e.name}</p>
                <p className="text-[10px] text-muted-foreground">{e.category}</p>
              </button>
            ))}
          </div>
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Exam date (optional)</label>
            <Input
              type="date"
              value={form.exam_date || ''}
              onChange={(e) => setForm({ ...form, exam_date: e.target.value })}
              data-testid="sp-exam-date"
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4" data-testid="sp-wizard-step-2">
          <div>
            <h2 className="text-xl font-bold">2 · How many hours each day?</h2>
            <p className="text-xs text-muted-foreground">Be honest — shorter but daily beats marathon cramming.</p>
          </div>
          <Slider
            label="Weekday hours" value={form.daily_hours_weekday}
            onChange={(v) => setForm({ ...form, daily_hours_weekday: v })}
            testId="sp-weekday-hours"
          />
          <Slider
            label="Weekend hours" value={form.daily_hours_weekend}
            onChange={(v) => setForm({ ...form, daily_hours_weekend: v })}
            testId="sp-weekend-hours"
          />
          <div>
            <label className="text-xs font-semibold text-muted-foreground">Active days</label>
            <div className="grid grid-cols-7 gap-1 mt-1">
              {WEEKDAYS.map((w, i) => (
                <button
                  key={w}
                  onClick={() => setForm({
                    ...form,
                    active_days: form.active_days.includes(i)
                      ? form.active_days.filter(x => x !== i)
                      : [...form.active_days, i],
                  })}
                  data-testid={`sp-day-${i}`}
                  className={`py-2 rounded-lg text-xs font-bold transition ${form.active_days.includes(i) ? 'bg-indigo-500 text-white' : 'border border-border text-muted-foreground'}`}
                >
                  {w}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-4" data-testid="sp-wizard-step-3">
          <div>
            <h2 className="text-xl font-bold">3 · Pick your study mix</h2>
            <p className="text-xs text-muted-foreground">Totals don't need to sum to 100 — we'll normalise.</p>
          </div>
          {Object.entries(TYPE_META).map(([k, meta]) => (
            <Slider
              key={k}
              label={meta.label}
              value={form.mix[k]} max={100} min={0} step={5} unit="%"
              onChange={(v) => setForm({ ...form, mix: { ...form.mix, [k]: v } })}
              testId={`sp-mix-${k}`}
              icon={meta.icon} color={meta.colour}
            />
          ))}
        </div>
      )}

      {step === 4 && (
        <div className="space-y-4" data-testid="sp-wizard-step-4">
          <div>
            <h2 className="text-xl font-bold">4 · Ready to start?</h2>
            <p className="text-xs text-muted-foreground">We'll generate a deterministic plan that repeats weekly — you can always edit it later.</p>
          </div>
          <div className="p-4 rounded-2xl border border-border bg-card space-y-1.5 text-sm">
            <p><b>Exam:</b> {form.exam_ids.join(', ')}</p>
            {form.exam_date && <p><b>Target date:</b> {form.exam_date}</p>}
            <p><b>Hours:</b> {form.daily_hours_weekday}h weekday · {form.daily_hours_weekend}h weekend</p>
            <p><b>Active days:</b> {form.active_days.map(d => WEEKDAYS[d]).join(', ')}</p>
            <p><b>Mix:</b> {Object.entries(form.mix).map(([k, v]) => `${TYPE_META[k].label} ${v}%`).join(' · ')}</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={step > 1 ? () => setStep(step - 1) : onCancel} data-testid="sp-wizard-back">
          <ChevronLeft className="w-4 h-4 mr-1" />{step > 1 ? 'Back' : 'Cancel'}
        </Button>
        {step < 4 ? (
          <Button size="sm" onClick={() => setStep(step + 1)} className="bg-indigo-500 hover:bg-indigo-600 text-white" data-testid="sp-wizard-next">
            Next<ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button size="sm" onClick={submit} disabled={submitting} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="sp-wizard-submit">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
            Create my plan
          </Button>
        )}
      </div>
    </div>
  );
}

function Slider({ label, value, min = 1, max = 14, step = 0.5, unit = 'h', onChange, testId, icon: Icon, color }) {
  return (
    <div>
      <div className="flex items-center justify-between text-xs">
        <span className="font-semibold flex items-center gap-1.5">
          {Icon && <span className={`w-5 h-5 rounded ${color} text-white flex items-center justify-center`}><Icon className="w-3 h-3" /></span>}
          {label}
        </span>
        <span className="font-bold">{value}{unit}</span>
      </div>
      <input
        type="range" min={min} max={max} step={step} value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-full accent-indigo-500"
        data-testid={testId}
      />
    </div>
  );
}

// ---------- Planned view ----------
function PlannedView({ plan, onEdit, onReset }) {
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [selected, setSelected] = useState(today);
  const [day, setDay] = useState(null);
  const [loading, setLoading] = useState(true);
  const [monthCursor, setMonthCursor] = useState(today.slice(0, 7));
  const [monthData, setMonthData] = useState(null);
  const [streak, setStreak] = useState(0);

  const loadDay = useCallback(async (d) => {
    setLoading(true);
    try {
      const r = await studyPlanApi.day(d);
      setDay(r);
    } catch { /* empty */ }
    setLoading(false);
  }, []);
  const loadMonth = useCallback(async (yyyyMm) => {
    try {
      const r = await studyPlanApi.month(yyyyMm);
      setMonthData(r);
    } catch { /* empty */ }
  }, []);

  useEffect(() => { loadDay(selected); }, [selected, loadDay]);
  useEffect(() => { loadMonth(monthCursor); }, [monthCursor, loadMonth]);
  useEffect(() => { studyPlanApi.streak().then(r => setStreak(r.streak || 0)); }, [day]);

  const toggleComplete = async (b) => {
    try {
      if (b.completed_at) {
        await studyPlanApi.uncomplete(selected, b.block_id);
      } else {
        await studyPlanApi.complete(selected, b.block_id);
      }
      loadDay(selected);
      loadMonth(monthCursor);
    } catch (e) { toast.error(e.message || 'Update failed'); }
  };

  const destroy = async () => {
    if (!window.confirm('Delete your plan? You can create a new one anytime.')) return;
    try {
      await studyPlanApi.remove();
      toast.success('Plan removed');
      onReset();
    } catch (e) { toast.error(e.message || 'Delete failed'); }
  };

  const [adapting, setAdapting] = useState(false);
  const adapt = async () => {
    if (!window.confirm('Refresh your topic pool by promoting weak areas (from your error book) to the top? Today\'s blocks are unchanged; tomorrow onwards will use the new ranking.')) return;
    setAdapting(true);
    try {
      const r = await studyPlanApi.refreshPool();
      toast.success(`Adapted! ${r.weak_topics_promoted} weak topic${r.weak_topics_promoted === 1 ? '' : 's'} promoted.`);
      loadDay(selected);
      loadMonth(monthCursor);
    } catch (e) { toast.error(e.message || 'Adapt failed'); }
    setAdapting(false);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5" data-testid="sp-planned">
      <div className="space-y-5">
        {/* Summary row */}
        <div className="flex items-center gap-3 flex-wrap">
          <Badge className="bg-indigo-500 text-white">{plan.exam_ids.join(' · ')}</Badge>
          {plan.exam_date && <Badge variant="outline" className="text-xs">Target: {plan.exam_date}</Badge>}
          <Badge variant="outline" className="text-xs">{plan.daily_hours_weekday}h weekday · {plan.daily_hours_weekend}h weekend</Badge>
          <Badge variant="outline" className="text-xs flex items-center gap-1">
            <Flame className="w-3 h-3 text-orange-500" />Streak: {streak}
          </Badge>
          <div className="ml-auto flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={adapt} disabled={adapting} className="border-amber-500/40 hover:bg-amber-500/10" data-testid="sp-adapt">
              {adapting ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Sparkles className="w-3.5 h-3.5 mr-1.5 text-amber-500" />}
              Adapt to weak areas
            </Button>
            <Button size="sm" variant="outline" onClick={onEdit} data-testid="sp-edit">
              <Edit3 className="w-3.5 h-3.5 mr-1.5" />Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={destroy} data-testid="sp-delete">
              <Trash2 className="w-3.5 h-3.5 text-rose-500" />
            </Button>
          </div>
        </div>

        {/* Today's (or selected day's) blocks */}
        <div className="p-5 rounded-2xl border border-border bg-card" data-testid="sp-day">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-bold">{selected === today ? "Today's plan" : `Plan for ${selected}`}</h2>
              <p className="text-[11px] text-muted-foreground">{day?.weekday}</p>
            </div>
            {day && day.total_min > 0 && (
              <div className="text-right">
                <p className="text-xs text-muted-foreground">{day.done_min} of {day.total_min} min</p>
                <Progress value={day.progress_pct} className="w-32 h-1.5 mt-1" />
              </div>
            )}
          </div>
          {loading ? (
            <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin" /></div>
          ) : !day?.blocks?.length ? (
            <p className="text-xs text-muted-foreground text-center py-6" data-testid="sp-day-off">Rest day. Recharge for tomorrow.</p>
          ) : (
            <div className="space-y-2" data-testid="sp-blocks">
              {day.blocks.map((b, i) => <BlockRow key={b.block_id} block={b} onToggle={() => toggleComplete(b)} index={i} />)}
            </div>
          )}
        </div>
      </div>

      {/* Calendar sidebar */}
      <div className="space-y-4" data-testid="sp-calendar">
        <MonthCalendar
          monthData={monthData}
          cursor={monthCursor}
          selected={selected}
          onPickDay={setSelected}
          onShift={(delta) => {
            const [y, m] = monthCursor.split('-').map(Number);
            const d = new Date(y, m - 1 + delta, 1);
            setMonthCursor(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
          }}
        />
        <Legend />
      </div>
    </div>
  );
}

function BlockRow({ block, onToggle, index }) {
  const meta = TYPE_META[block.type] || TYPE_META.learning;
  const Icon = meta.icon;
  const done = !!block.completed_at;
  return (
    <div className={`p-3 rounded-xl border flex items-center gap-3 ${done ? 'border-emerald-300 bg-emerald-50/30 dark:bg-emerald-950/10' : 'border-border bg-background/50'}`} data-testid={`sp-block-${index}`}>
      <button
        onClick={onToggle}
        className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${done ? 'bg-emerald-500 text-white' : 'border-2 border-border hover:border-indigo-400'}`}
        data-testid={`sp-block-toggle-${index}`}
      >
        {done ? <CheckCircle2 className="w-4 h-4" /> : <Icon className="w-4 h-4 text-muted-foreground" />}
      </button>
      <div className="flex-1 min-w-0">
        <p className={`font-semibold text-sm truncate ${done ? 'line-through text-muted-foreground' : ''}`}>{block.label}</p>
        <div className="flex items-center gap-1.5 mt-0.5">
          <Badge className={`${meta.colour} text-white text-[10px]`}>{meta.label}</Badge>
          <Badge variant="outline" className="text-[10px]">{block.duration_min} min</Badge>
          <Badge variant="outline" className="text-[10px]">{block.exam_id}</Badge>
        </div>
      </div>
      <a
        href={block.link}
        onClick={(e) => {
          // Mark complete when student clicks Start — gentle auto-check
          if (!done) {
            setTimeout(() => onToggle(), 400);
          }
          e.stopPropagation();
        }}
        data-testid={`sp-block-start-${index}`}
      >
        <Button size="sm" variant={done ? 'ghost' : 'default'} className={done ? '' : 'bg-indigo-500 hover:bg-indigo-600 text-white'}>
          {done ? 'Review' : <><Play className="w-3.5 h-3.5 mr-1" />Start</>}
        </Button>
      </a>
    </div>
  );
}

function MonthCalendar({ monthData, cursor, selected, onPickDay, onShift }) {
  const [y, m] = cursor.split('-').map(Number);
  const first = new Date(y, m - 1, 1);
  const firstDay = (first.getDay() + 6) % 7; // Monday=0
  const daysInMonth = new Date(y, m, 0).getDate();
  const monthLabel = first.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
  const byDate = {};
  (monthData?.days || []).forEach(d => { byDate[d.date] = d; });

  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  return (
    <div className="p-4 rounded-2xl border border-border bg-card">
      <div className="flex items-center justify-between mb-3">
        <Button variant="ghost" size="icon" onClick={() => onShift(-1)} data-testid="sp-cal-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <p className="font-bold text-sm">{monthLabel}</p>
        <Button variant="ghost" size="icon" onClick={() => onShift(1)} data-testid="sp-cal-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 mb-1">
        {WEEKDAYS.map(w => <div key={w} className="text-center text-[10px] font-semibold text-muted-foreground">{w}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (!d) return <div key={i} />;
          const iso = `${cursor}-${String(d).padStart(2, '0')}`;
          const info = byDate[iso];
          const pct = info?.total_min ? Math.round(100 * info.done_min / info.total_min) : 0;
          const isSelected = iso === selected;
          const isToday = info?.is_today;
          const bg = info?.block_count
            ? (pct >= 100 ? 'bg-emerald-500 text-white' : pct > 0 ? 'bg-emerald-200 dark:bg-emerald-900 text-emerald-900 dark:text-emerald-100' : 'bg-indigo-100 dark:bg-indigo-950/50 text-foreground')
            : 'text-muted-foreground';
          return (
            <button
              key={i}
              onClick={() => onPickDay(iso)}
              data-testid={`sp-cal-day-${iso}`}
              className={`aspect-square rounded-lg text-[11px] font-semibold transition ${bg} ${isSelected ? 'ring-2 ring-indigo-500' : ''} ${isToday ? 'border-2 border-indigo-500' : ''}`}
              title={info?.block_count ? `${info.block_count} blocks · ${info.total_min} min · ${pct}% done` : 'Rest day'}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function Legend() {
  return (
    <div className="p-4 rounded-2xl border border-border bg-card text-[11px] space-y-1.5">
      <p className="font-bold mb-1.5">Legend</p>
      <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-indigo-100 dark:bg-indigo-950/50" /><span>Planned (not started)</span></div>
      <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-emerald-200 dark:bg-emerald-900" /><span>Partly done</span></div>
      <div className="flex items-center gap-2"><div className="w-4 h-4 rounded bg-emerald-500" /><span>All done ✓</span></div>
      <div className="flex items-center gap-2"><div className="w-4 h-4 rounded border-2 border-indigo-500" /><span>Today</span></div>
    </div>
  );
}
